import {
	useState,
	createContext,
	useContext,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import { getCustomerDetails } from "../APICalling/APICalling";
import logger from "../Utils/logger";
import { defaultStatus } from "../Utils/Constants/defaultStatus";
import { findAssignedOutletById } from "../Utils/utils";

const GlobalContext = createContext();

/** Per-user last chosen outlet (survives refresh; cleared on logout via localStorage.clear). */
const PERSISTED_SELECTED_OUTLET_LS_PREFIX = "nlo_selected_outlet_id_";

function getPersistedSelectedOutletStorageKey(userId) {
	if (!userId || userId === "null") return null;
	return `${PERSISTED_SELECTED_OUTLET_LS_PREFIX}${userId}`;
}

function readInitialOutletSelectionFromStorage() {
	if (typeof window === "undefined") {
		return { outletId: null, outletData: null };
	}
	let persistedId = null;
	try {
		const uid = localStorage.getItem("user_id");
		const key = getPersistedSelectedOutletStorageKey(uid);
		if (key) {
			const raw = localStorage.getItem(key);
			if (raw != null && raw !== "") {
				const n = Number(raw);
				if (Number.isFinite(n) && n > 0) persistedId = n;
			}
		}
	} catch {
		// ignore
	}

	let outletData = null;
	try {
		const saved = localStorage.getItem("selected_outlet");
		if (saved) {
			const parsed = JSON.parse(saved);
			if (parsed?.id != null) {
				const parsedId = Number(parsed.id);
				if (Number.isFinite(parsedId) && parsedId > 0) {
					if (persistedId == null) {
						persistedId = parsedId;
						outletData = parsed;
					} else if (parsedId === persistedId) {
						outletData = parsed;
					}
				}
			}
		}
	} catch {
		// ignore
	}

	if (outletData == null && persistedId != null) {
		try {
			const assigned = JSON.parse(localStorage.getItem("user_assigned_outlets") || "[]");
			outletData = findAssignedOutletById(assigned, persistedId);
		} catch {
			// ignore
		}
	}

	return { outletId: persistedId, outletData };
}

/** API default (`outlet_id`) when still assigned, else first edit outlet, else first assigned. */
function pickDefaultOutletId(userAssignedOutlets, loggedOutletId) {
	if (!userAssignedOutlets?.length) return null;
	const assignedIds = new Set(userAssignedOutlets.map((o) => Number(o.id)));
	const normalizedLoggedId = loggedOutletId != null ? Number(loggedOutletId) : null;
	if (
		normalizedLoggedId != null &&
		Number.isFinite(normalizedLoggedId) &&
		assignedIds.has(normalizedLoggedId)
	) {
		return normalizedLoggedId;
	}
	const firstEdit = userAssignedOutlets.find((o) => o.mode === "edit");
	if (firstEdit?.id != null) return Number(firstEdit.id);
	const first = userAssignedOutlets[0]?.id;
	return first != null ? Number(first) : null;
}

function readJSONFromLocalStorage(key, defaultValue = null) {
	if (typeof window === "undefined") return defaultValue;
	try {
		const value = window.localStorage.getItem(key);
		return value ? JSON.parse(value) : defaultValue;
	} catch (error) {
		logger.error(`Failed to read ${key} from localStorage`, error);
		return defaultValue;
	}
}

function readTempBookingIdFromStorage() {
	if (typeof window === "undefined") return null;
	try {
		const id = window.localStorage.getItem("tempBookingID");
		return id && id !== "null" ? Number(id) : null;
	} catch (error) {
		logger.error("Failed to read tempBookingID from localStorage", error);
		return null;
	}
}

export const GlobalProvider = ({ children }) => {
	// Read once per mount — both selected-outlet states need the same snapshot.
	const initialOutletSelectionRef = useRef(null);
	if (initialOutletSelectionRef.current === null) {
		initialOutletSelectionRef.current = readInitialOutletSelectionFromStorage();
	}
	const initialOutletSelection = initialOutletSelectionRef.current;

	const [staffData, setStaffData] = useState([]);
	const [outletList, setOutletList] = useState(() => {
		const saved = localStorage.getItem("outlet_list");
		return saved ? JSON.parse(saved) : [];
	});

	const OTP_VALIDITY_DURATION = 45; // in seconds
	const [otpTimer, setOtpTimer] = useState(0);
	const [isUserVerified, setIsUserVerified] = useState(false);

	const [bookingData, setBookingData] = useState({
		outlet: null,
		date: null,
		time: {
			label: "",
			value: "",
		},
	});

	const [items, setItems] = useState([]);

	const [itemData, setItemData] = useState(null);
	const [bookingsList, setBookingsList] = useState([]);
	const [customerData, setCustomerData] = useState({});
	const [bookingType, setBookingType] = useState(1);

	const [loggedOutletID, setLoggedOutletID] = useState(() => {
		const outletId = localStorage.getItem("outlet_id");
		return outletId ? Number(outletId) : null;
	});

	const [outletData, setOutletData] = useState([]);
	const [selectedOutletName, setSelectedOutletName] = useState(null);
	const [loggedUser, setLoggedUser] = useState(() => {
		const userId = localStorage.getItem("user_id");
		return userId ? Number(userId) : null;
	});
	const [loggedName, setLoggedName] = useState(() => {
		const name = localStorage.getItem("user_name");
		return name ? JSON.parse(name) : null;
	});
	const [loggedEmail, setLoggedEmail] = useState(() => {
		const email = localStorage.getItem("user_email");
		return email || null;
	});
	const [userName, setUserName] = useState(() => readJSONFromLocalStorage("user_name"));
	const [userAssignedOutlets, setUserAssignedOutlets] = useState(() => {
		const saved = localStorage.getItem("user_assigned_outlets");
		return saved ? JSON.parse(saved) : [];
	});


	const [outletOptions, setOutletOptions] = useState([]);
	const [selectedOutletData, setSelectedOutletData] = useState(initialOutletSelection.outletData);
	const [selectedOutlet, setSelectedOutlet] = useState(initialOutletSelection.outletId);
	const [filteredBookings, setFilteredBookings] = useState([]);
	const [apiError, setApiError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [isFormValid, setIsFormValid] = useState(false);
	const [newAppointmentSlots, setNewAppointmentSlots] = useState([]); // one for each service
	const [newSlot, setNewSlot] = useState(null);
	const [pendingBookings, setPendingBookings] = useState([]); // placeholder bookings while waiting for API/socket response
	const [outletSelected, setOutletSelected] = useState([]);
	const [homeStaffList, setHomeStaffList] = useState([]);
	const [selectedServices, setSelectedServices] = useState([]);
	const [triggerCancel, setTriggerCancel] = useState(false);
	const [paxes, setPaxes] = useState([]);
	const [bookingChanged, setBookingChanged] = useState(false);
	const [deletedBookingItemIds, setDeletedBookingItemIds] = useState([]);

	const [outletAccessMap, setOutletAccessMap] = useState({});
	const selectedOutletAccess = outletAccessMap[selectedOutlet];
	const isViewOnlyOutlet = selectedOutletAccess === "view";

	// Persist last outlet choice per logged-in user (restored on refresh; cleared on logout).
	useEffect(() => {
		if (typeof window === "undefined") return;
		const uid = localStorage.getItem("user_id");
		const key = getPersistedSelectedOutletStorageKey(uid);
		if (!key) return;
		if (selectedOutlet == null) {
			localStorage.removeItem(key);
			return;
		}
		localStorage.setItem(key, String(selectedOutlet));
	}, [selectedOutlet]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (selectedOutletData?.id != null) {
			localStorage.setItem("selected_outlet", JSON.stringify(selectedOutletData));
			return;
		}
		localStorage.removeItem("selected_outlet");
	}, [selectedOutletData]);

	// Reconcile selection with fresh outlet_details (revoked / deleted / no access).
	useEffect(() => {
		if (!userAssignedOutlets?.length) {
			if (selectedOutlet != null) {
				try {
					const uid = localStorage.getItem("user_id");
					const key = getPersistedSelectedOutletStorageKey(uid);
					if (key) localStorage.removeItem(key);
				} catch {
					// ignore
				}
				setSelectedOutlet(null);
				setSelectedOutletData(null);
			}
			return;
		}

		const assignedIds = new Set(userAssignedOutlets.map((o) => Number(o.id)));
		const normalizedSelected =
			selectedOutlet != null ? Number(selectedOutlet) : null;
		const currentIsValid =
			normalizedSelected != null &&
			Number.isFinite(normalizedSelected) &&
			assignedIds.has(normalizedSelected);

		if (currentIsValid) {
			setSelectedOutletData((prev) => {
				if (prev != null && Number(prev.id) === normalizedSelected) return prev;
				return findAssignedOutletById(userAssignedOutlets, normalizedSelected) || prev;
			});
			return;
		}

		const fallbackId = pickDefaultOutletId(userAssignedOutlets, loggedOutletID);

		try {
			const uid = localStorage.getItem("user_id");
			const key = getPersistedSelectedOutletStorageKey(uid);
			if (
				key &&
				normalizedSelected != null &&
				!assignedIds.has(normalizedSelected)
			) {
				localStorage.removeItem(key);
			}
		} catch {
			// ignore
		}

		if (fallbackId !== normalizedSelected) {
			setSelectedOutlet(fallbackId);
			setSelectedOutletData((prev) =>
				prev != null && Number(prev.id) === Number(fallbackId)
					? prev
					: findAssignedOutletById(userAssignedOutlets, fallbackId)
			);
		}
	}, [userAssignedOutlets, loggedOutletID, selectedOutlet]);

	const [isCustomerSelected, setIsCustomerSelected] = useState(false);
	const [isSourceSelected, setIsSourceSelected] = useState(false);
	const [isSidebarVisible, setIsSidebarVisible] = useState(false);
	const [editPrefill, setEditPrefill] = useState(null);
	const [editLoading, setEditLoading] = useState(false);
	const [completeBookingWarning, setCompleteBookingWarning] = useState(false);
	const [showPaymentInProgressNotice, setShowPaymentInProgressNotice] = useState(false);
	const resetBookingForm = useCallback(() => {
		const today = new Date().toString();
		setBookingData({
			date: today,
			time: {
				label: "",
				value: "",
			},
			therapist: null,
		});
		setItemData(null);
		setItems([]);
	}, []);

	// Payment related Code
	const [serviceTotal, setServiceTotal] = useState(0);

	const [paymentSummary, setPaymentSummary] = useState({
		subtotal: 0,
		midtotal: 0,
		gst: 0,
		tip: "0.00",
		total: 0,
		payment_method: "",
	});
	const [paymentHistory, setPaymentHistory] = useState([]);
	const [currentPaymentMethod, setCurrentPaymentMethod] = useState("");
	const [currentPaymentMethodTitle, setCurrentPaymentMethodTitle] = useState("");
	const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState(null);
	const [amountDue, setAmountDue] = useState(0);
	const [amountToCharge, setAmountToCharge] = useState("");
	const [reference, setReference] = useState("");
	const [customerCreditDetails, setCustomerCreditDetails] = useState([]);

	const [editedItemData, setEditedItemData] = useState({
		company: 1,
		panel: "outlet",
		currency: "SGD",
	});

	useEffect(() => {
		if (loggedUser != null) {
			setEditedItemData((prev) =>
				prev.handle_by === loggedUser ? prev : { ...prev, handle_by: loggedUser }
			);
		}
	}, [loggedUser]);


	const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
	const [deletedBookingID, setDeletedBookingID] = useState(null);
	const [isNewBookingSlotActive, setIsNewBookingSlotActive] = useState(false);
	const [isSidebarLoading, setIsSidebarLoading] = useState(false);
	useEffect(() => {
		if (isSidebarLoading) {
			const timer = setTimeout(() => {
				setIsSidebarLoading(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [isSidebarLoading]);
	const [selectedBookingId, setSelectedBookingId] = useState(null);
	const [selectedSaleId, setSelectedSaleId] = useState(null);
	const [selectedBookingData, setSelectedBookingData] = useState([]);
	const [selectedBookingDate, setSelectedBookingDate] = useState(null);
	const [selectedDate, setSelectedDate] = useState(() => new Date());
	const [calendarDateSyncSignal, setCalendarDateSyncSignal] = useState({
		requestId: 0,
		mode: null,
		status: "idle",
		date: null,
		refreshKey: 0,
	});
	const [dateSyncState, setDateSyncState] = useState({
		requestId: null,
		mode: null,
		status: "idle",
		date: null,
	});
	const [dateSyncContextData, setDateSyncContextData] = useState(null);
	const [selectedBookingDeleteScope, setSelectedBookingDeleteScope] = useState(null);
	const [selectedBookingUpdateScope, setSelectedBookingUpdateScope] = useState(null);
	const [selectedTimeNew, setSelectedTimeNew] = useState({});
	const [selectedTime, setSelectedTime] = useState(() => bookingData?.time || null);
	const [isEditBookingActive, setIsEditBookingActive] = useState(false);
	const [activeSlotId, setActiveSlotId] = useState(null);
	const [createdBooking, setCreatedBooking] = useState(null);
	const [selectedReason, setSelectedReason] = useState("");
	const [selectedRemark, setSelectedRemark] = useState("");
	const [intervalClass, setIntervalClass] = useState("smallInterval");
	const [tableInterval, setTableInterval] = useState(15);
	const [timeSlots, setTimeSlots] = useState([]);
	const [selectedRoomFilter, setSelectedRoomFilter] = useState("");
	const [selectedOutletTiming, setSelectedOutletTiming] = useState([]);
	const [selectedTherapistIDFromRow, setSelectedTherapistIDFromRow] = useState(null);
	const [selectedTherapistFromRow, setSelectedTherapistFromRow] = useState(null);
	const [showCancelPopup, setShowCancelPopup] = useState(false);
	const [voucherDetail, setVoucherDetail] = useState([]);
	const [voucherStates, setVoucherStates] = useState({});
	const [discountByBookingItemId, setDiscountByBookingItemId] = useState({});
	const [manualCommissionByBookingItemId, setManualCommissionByBookingItemId] = useState({});
	const [clearedEditedCommissionByBookingItemId, setClearedEditedCommissionByBookingItemId] = useState({});
	const [discountItemToPost, setDiscountItemToPost] = useState([]);
	const [memberRateByBookingItemId, setMemberRateByBookingItemId] = useState({});
	const [colIndexWhenTherapistSelect, setColIndexWhenTherapistSelect] = useState(null);
	const [isMembershipApplied, setIsMembershipApplied] = useState(false);
	const [isBookingListUpdated, setIsBookingListUpdated] = useState(false);
	const [isBookingInfoUpdated, setIsBookingInfoUpdated] = useState(false);
	const [showBookingSummary, setShowBookingSummary] = useState(false);
	const [sidebarLoading, setSidebarLoading] = useState(false);
	const [errorList, setErrorList] = useState([]);
	const [isDateChange, setIsDateChange] = useState(false);
	const [isTimeChange, setIsTimeChange] = useState(false);
	const [isCustomerChange, setIsCustomerChange] = useState(false);
	const [isMembershipChange, setIsMembershipChange] = useState(false);
	const [showConflictModal, setShowConflictModal] = useState(false);
	const [isGreyBox, setIsGreyBox] = useState(false);
	const [pendingTherapistSelection, setPendingTherapistSelection] = useState(null);
	const [noRoomSelected, setNoRoomSelected] = useState(true);
	const [therapistLoading, setTherapistLoading] = useState(false);
	const [newBookingActive, setNewBookingActive] = useState(false);
	const [isRoomFilterActive, setIsRoomFilterActive] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);

	const [homeRoomList, setHomeRoomList] = useState([]);
	const [selectedRooms, setSelectedRooms] = useState({});
	const [selectedSource, setSelectedSource] = useState(null);
	const [filteredItemIds, setFilteredItemIds] = useState([]);
	const [filteredRoomIds, setFilteredRoomIds] = useState([]);

	const [bookingFilters, setBookingFilters] = useState({
		gender: [],
		status: defaultStatus,
		staffIds: [],
		roomItem: [],
		roomIds: [],
		isFilterApplied: isFilterApplied,
	});

	const [hitTemporaryBookingApi, setHitTemporaryBookingApi] = useState(false);
	const [tempBookingCreated, setTempBookingCreated] = useState(false);
	const [temporaryBookingId, setTemporaryBookingId] = useState(() => readTempBookingIdFromStorage());
	const [publicHoliday, setPublicHoliday] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return undefined;
		const handleStorage = (event) => {
			if (event.key !== "tempBookingID") return;
			const storedId = readTempBookingIdFromStorage();
			setTemporaryBookingId((prev) => (prev === storedId ? prev : storedId));
		};
		window.addEventListener("storage", handleStorage);
		return () => window.removeEventListener("storage", handleStorage);
	}, []);


	// SESSION ID START
	const [localSessionId, setLocalSessionId] = useState(
		sessionStorage.getItem("session_id") || null
	);

	useEffect(() => {
		if (!localSessionId) {
		const newId = crypto.randomUUID();
		sessionStorage.setItem("session_id", newId);
		setLocalSessionId(newId);
		}
	}, [localSessionId]);

	// Fetch and update user details on app initialization or reload
	useEffect(() => {
		const fetchUserDetails = async () => {
			const token = localStorage.getItem("token");
			const userId = localStorage.getItem("user_id");

			if (!token || !userId || userId === "null") return;

			try {
				const response = await getCustomerDetails(userId);
				if (response?.data?.success && response?.data?.data) {
					const user = response.data.data;

					localStorage.setItem("outlet_id", String(user.outlet_id));
					localStorage.setItem("user_id", String(user.id));
					localStorage.setItem("user_name", JSON.stringify(user.name));
					localStorage.setItem("user_email", String(user.email));
					localStorage.setItem("user_assigned_outlets", JSON.stringify(user.outlet_details || []));

					setLoggedOutletID(user.outlet_id);
					setLoggedUser(user.id);
					setLoggedName(user.name);
					setLoggedEmail(user.email);
					setUserAssignedOutlets(user.outlet_details || []);
					setUserName(user.name);
				}
			} catch (error) {
				logger.error("Failed to fetch user details:", error);
			}
		};

		fetchUserDetails();
	}, []);

	// Listen for logout event from other tabs
	useEffect(() => {
		const channel = new BroadcastChannel('auth_channel');
		channel.onmessage = (event) => {
			if (event.data === 'logout') {
				localStorage.clear();
				window.location.replace("/login");
			}
		};
		return () => {
			channel.close();
		};
	}, []);

	const [bookingDataFromAPI, setBookingDataFromAPI] = useState(null);
	const [customerDataFromAPI, setCustomerDataFromAPI] = useState([]);
	const [bookingDetails, setBookingDetails] = useState(null);
	const [customerBookingItems, setCustomerBookingItems] = useState(null);
	const [editableBooking, setEditableBooking] = useState(null);
	const [guestCheckinList, setGuestCheckinList] = useState([]);
	const [selectedGuestCheckin, setSelectedGuestCheckin] = useState({}); // Object with keys: 'customer', 'pax1', 'pax2', etc.
	const [showFilterPanel, setShowFilterPanel] = useState(false);
	const [isBookingSidebar, setIsBookingSidebar] = useState(false);
	const [isAutoApply, setIsAutoApply] = useState(false);
	const filterPanelRef = useRef(null);

	const [globalTherapistList, setGlobalTherapistList] = useState({
		outletId: null,
		data: [],
		archivedData: [],
	});


	console.log("selectedBookingId", selectedBookingId);
	
	const contextValue = useMemo(
		() => ({
			globalTherapistList, setGlobalTherapistList,
			formErrors, setFormErrors,
			staffData, setStaffData,
			bookingData, setBookingData,
			isUserVerified, setIsUserVerified,
			otpTimer, setOtpTimer,
			OTP_VALIDITY_DURATION,
			customerData, setCustomerData,
			loggedOutletID, setLoggedOutletID,
			loggedUser, setLoggedUser,
			loggedName, setLoggedName,
			loggedEmail, setLoggedEmail,
			outletList, setOutletList,
			bookingsList, setBookingsList,
			filteredBookings, setFilteredBookings,
			isFormValid, setIsFormValid,
			apiError, setApiError,
			items, setItems,
			itemData, setItemData,
			resetBookingForm,
			newAppointmentSlots, setNewAppointmentSlots,
			newSlot, setNewSlot,
			pendingBookings, setPendingBookings,
			outletSelected, setOutletSelected,
			bookingType, setBookingType,
			outletOptions, setOutletOptions,
			selectedOutlet, setSelectedOutlet,
			userAssignedOutlets, setUserAssignedOutlets,
			homeStaffList, setHomeStaffList,
			// homeStaffListUpdated, setHomeStaffListUpdated,
			selectedServices, setSelectedServices,
			triggerCancel, setTriggerCancel,
			serviceTotal, setServiceTotal,
			paymentSummary, setPaymentSummary,
			paymentHistory, setPaymentHistory,
			amountDue, setAmountDue,
			amountToCharge, setAmountToCharge,
			currentPaymentMethod, setCurrentPaymentMethod,
			currentPaymentMethodTitle, setCurrentPaymentMethodTitle,
			currentPaymentMethodId, setCurrentPaymentMethodId,
			reference, setReference,
			paxes, setPaxes,
			editedItemData, setEditedItemData,
			bookingChanged, setBookingChanged,
			deletedBookingItemIds, setDeletedBookingItemIds,
			userName, setUserName,
			rightSidebarOpen, setRightSidebarOpen,
			deletedBookingID, setDeletedBookingID,
			isNewBookingSlotActive, setIsNewBookingSlotActive,
			isSidebarLoading, setIsSidebarLoading,
			selectedBookingId, setSelectedBookingId,
			selectedSaleId, setSelectedSaleId,
			selectedBookingData, setSelectedBookingData,
			selectedBookingDate, setSelectedBookingDate,
			selectedBookingDeleteScope, setSelectedBookingDeleteScope,
			selectedBookingUpdateScope, setSelectedBookingUpdateScope,
			selectedTime, setSelectedTime,
			calendarDateSyncSignal, setCalendarDateSyncSignal,
			dateSyncState, setDateSyncState,
			dateSyncContextData, setDateSyncContextData,
			isEditBookingActive, setIsEditBookingActive,
			createdBooking, setCreatedBooking,
			selectedReason, setSelectedReason,
			selectedRemark, setSelectedRemark,
			outletData, setOutletData,
			selectedOutletName, setSelectedOutletName,
			selectedDate, setSelectedDate,
			intervalClass, setIntervalClass,
			tableInterval, setTableInterval,
			timeSlots, setTimeSlots,
			bookingFilters, setBookingFilters,
			selectedOutletTiming, setSelectedOutletTiming,
			selectedOutletData, setSelectedOutletData,
			outletAccessMap, setOutletAccessMap,
			selectedOutletAccess,
			isViewOnlyOutlet,
			isCustomerSelected, setIsCustomerSelected,
			isSourceSelected, setIsSourceSelected,
			selectedTherapistIDFromRow, setSelectedTherapistIDFromRow,
			selectedTherapistFromRow, setSelectedTherapistFromRow,
			voucherDetail, setVoucherDetail,
			voucherStates, setVoucherStates,
			discountByBookingItemId, setDiscountByBookingItemId,
			manualCommissionByBookingItemId, setManualCommissionByBookingItemId,
			clearedEditedCommissionByBookingItemId, setClearedEditedCommissionByBookingItemId,
			memberRateByBookingItemId, setMemberRateByBookingItemId,
			colIndexWhenTherapistSelect, setColIndexWhenTherapistSelect,
			isSidebarVisible, setIsSidebarVisible,
			isMembershipApplied, setIsMembershipApplied,
			isBookingListUpdated, setIsBookingListUpdated,
			isBookingInfoUpdated, setIsBookingInfoUpdated,
			showBookingSummary, setShowBookingSummary,
			sidebarLoading, setSidebarLoading,
			errorList, setErrorList,
			selectedTimeNew, setSelectedTimeNew,
			isDateChange, setIsDateChange,
			isTimeChange, setIsTimeChange,
			isCustomerChange, setIsCustomerChange,
			isMembershipChange, setIsMembershipChange,
			editPrefill, setEditPrefill,
			showConflictModal, setShowConflictModal,
			completeBookingWarning, setCompleteBookingWarning,
			showPaymentInProgressNotice, setShowPaymentInProgressNotice,
			isGreyBox, setIsGreyBox,
			pendingTherapistSelection, setPendingTherapistSelection,
			noRoomSelected, setNoRoomSelected,
			therapistLoading, setTherapistLoading,
			isRoomFilterActive, setIsRoomFilterActive,
			homeRoomList, setHomeRoomList,
			selectedRooms, setSelectedRooms,
			selectedSource, setSelectedSource,
			filteredItemIds, setFilteredItemIds,
			filteredRoomIds, setFilteredRoomIds,
			selectedRoomFilter, setSelectedRoomFilter,
			isFilterApplied, setIsFilterApplied,
			hitTemporaryBookingApi, setHitTemporaryBookingApi,
			localSessionId, setLocalSessionId,
			temporaryBookingId, setTemporaryBookingId,
			tempBookingCreated, setTempBookingCreated,
			publicHoliday, setPublicHoliday,
			activeSlotId, setActiveSlotId,
			editableBooking, setEditableBooking,
			bookingDetails, setBookingDetails,
			guestCheckinList, setGuestCheckinList,
			selectedGuestCheckin, setSelectedGuestCheckin,
			showFilterPanel, setShowFilterPanel,
			isBookingSidebar, setIsBookingSidebar,
			editLoading, setEditLoading,
			showCancelPopup, setShowCancelPopup,
			filterPanelRef,
			bookingDataFromAPI, setBookingDataFromAPI,
			customerDataFromAPI, setCustomerDataFromAPI,
			newBookingActive, setNewBookingActive,
			discountItemToPost, setDiscountItemToPost,
			customerBookingItems, setCustomerBookingItems,
			customerCreditDetails, setCustomerCreditDetails,
			isAutoApply, setIsAutoApply,
		}),
		[
			globalTherapistList,
			formErrors,
			staffData,
			bookingData,
			isUserVerified,
			otpTimer,
			customerData,
			loggedOutletID,
			loggedUser,
			loggedName,
			loggedEmail,
			outletList,
			bookingsList,
			filteredBookings,
			isFormValid,
			apiError,
			items,
			itemData,
			resetBookingForm,
			newAppointmentSlots,
			newSlot,
			pendingBookings,
			outletSelected,
			bookingType,
			outletOptions,
			selectedOutlet,
			userAssignedOutlets,
			homeStaffList,
			selectedServices,
			triggerCancel,
			serviceTotal,
			paymentSummary,
			paymentHistory,
			amountDue,
			amountToCharge,
			currentPaymentMethod,
			currentPaymentMethodTitle,
			currentPaymentMethodId,
			reference,
			paxes,
			editedItemData,
			bookingChanged,
			deletedBookingItemIds,
			userName,
			rightSidebarOpen,
			deletedBookingID,
			isNewBookingSlotActive,
			isSidebarLoading,
			selectedBookingId,
			selectedSaleId,
			selectedBookingData,
			selectedBookingDate,
			selectedBookingDeleteScope,
			selectedBookingUpdateScope,
			selectedTime,
			calendarDateSyncSignal,
			dateSyncState,
			dateSyncContextData,
			isEditBookingActive,
			createdBooking,
			selectedReason,
			selectedRemark,
			outletData,
			selectedOutletName,
			selectedDate,
			intervalClass,
			tableInterval,
			timeSlots,
			bookingFilters,
			selectedOutletTiming,
			selectedOutletData,
			outletAccessMap,
			selectedOutletAccess,
			isViewOnlyOutlet,
			isCustomerSelected,
			isSourceSelected,
			selectedTherapistIDFromRow,
			selectedTherapistFromRow,
			voucherDetail,
			voucherStates,
			discountByBookingItemId,
			manualCommissionByBookingItemId,
			clearedEditedCommissionByBookingItemId,
			memberRateByBookingItemId,
			colIndexWhenTherapistSelect,
			isSidebarVisible,
			isMembershipApplied,
			isBookingListUpdated,
			isBookingInfoUpdated,
			showBookingSummary,
			sidebarLoading,
			errorList,
			selectedTimeNew,
			isDateChange,
			isTimeChange,
			isCustomerChange,
			isMembershipChange,
			editPrefill,
			showConflictModal,
			completeBookingWarning,
			showPaymentInProgressNotice, setShowPaymentInProgressNotice,
			isGreyBox,
			pendingTherapistSelection,
			noRoomSelected,
			therapistLoading,
			isRoomFilterActive,
			homeRoomList,
			selectedRooms,
			selectedSource,
			filteredItemIds,
			filteredRoomIds,
			selectedRoomFilter,
			isFilterApplied,
			hitTemporaryBookingApi,
			localSessionId,
			temporaryBookingId,
			tempBookingCreated,
			publicHoliday,
			activeSlotId,
			editableBooking,
			bookingDetails,
			guestCheckinList,
			selectedGuestCheckin,
			showFilterPanel,
			isBookingSidebar,
			editLoading,
			showCancelPopup,
			bookingDataFromAPI,
			customerDataFromAPI,
			newBookingActive,
			discountItemToPost,
			customerBookingItems,
			customerCreditDetails,
			isAutoApply,
		]
	);

	return (
		<GlobalContext.Provider value={contextValue}>
			{children}
		</GlobalContext.Provider>
	);
};
export const useGlobalContext = () => useContext(GlobalContext);
